import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Route, MapPin, Lock, Zap, Shield, AlertTriangle, CheckCircle, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function pointToSegmentDist(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number
): number {
  const dx = bx - ax, dy = by - ay;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy || 1)));
  return haversine(px, py, ax + t * dx, ay + t * dy);
}

interface Location {
  lat: number; lng: number; name: string;
}

const EVACUATION_CORRIDORS = [
  { name: "Kyiv → Lviv (Western Ukraine)", from: { lat: 50.45, lng: 30.52 }, to: { lat: 49.84, lng: 24.03 } },
  { name: "Kharkiv → Dnipro", from: { lat: 49.99, lng: 36.23 }, to: { lat: 48.46, lng: 35.05 } },
  { name: "Gaza → Egypt (Rafah)", from: { lat: 31.52, lng: 34.44 }, to: { lat: 31.28, lng: 34.24 } },
  { name: "Beirut → Tripoli", from: { lat: 33.89, lng: 35.50 }, to: { lat: 34.44, lng: 35.83 } },
  { name: "Damascus → Amman", from: { lat: 33.51, lng: 36.29 }, to: { lat: 31.95, lng: 35.93 } },
  { name: "Baghdad → Erbil", from: { lat: 33.31, lng: 44.37 }, to: { lat: 36.19, lng: 44.01 } },
];

const SafeRoutes = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const [fromLoc, setFromLoc] = useState<Location | null>(null);
  const [toLoc, setToLoc] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);

  const geocode = async (query: string): Promise<Location | null> => {
    const { data, error } = await supabase.functions.invoke("geocode", { body: { query } });
    if (error || !data?.found) return null;
    return { lat: data.lat, lng: data.lng, name: data.name };
  };

  const resolveRoute = async () => {
    if (!fromInput.trim() || !toInput.trim()) {
      toast.error("Enter both origin and destination");
      return;
    }
    setLoading(true);
    const [from, to] = await Promise.all([geocode(fromInput), geocode(toInput)]);
    if (!from) { toast.error("Origin not found"); setLoading(false); return; }
    if (!to) { toast.error("Destination not found"); setLoading(false); return; }
    setFromLoc(from);
    setToLoc(to);
    toast.success(`Route: ${from.name} → ${to.name}`);
    setLoading(false);
  };

  const useEvacCorridor = (corridor: typeof EVACUATION_CORRIDORS[0]) => {
    setFromLoc({ ...corridor.from, name: corridor.name.split(" → ")[0] });
    setToLoc({ ...corridor.to, name: corridor.name.split(" → ")[1]?.split(" (")[0] || "" });
    setFromInput(corridor.name.split(" → ")[0]);
    setToInput(corridor.name.split(" → ")[1]?.split(" (")[0] || "");
  };

  const analysis = useMemo(() => {
    if (!fromLoc || !toLoc || !events?.length) return null;

    const CORRIDOR_KM = 100;
    const directDist = haversine(fromLoc.lat, fromLoc.lng, toLoc.lat, toLoc.lng);

    const corridorThreats = events
      .filter((e) => e.lat && e.lng)
      .map((e) => ({
        ...e,
        distToRoute: pointToSegmentDist(e.lat!, e.lng!, fromLoc.lat, fromLoc.lng, toLoc.lat, toLoc.lng),
      }))
      .filter((e) => e.distToRoute <= CORRIDOR_KM)
      .sort((a, b) => a.distToRoute - b.distToRoute);

    const missileCount = corridorThreats.filter((e) => e.event_type === "missile").length;
    const droneCount = corridorThreats.filter((e) => e.event_type === "drone").length;
    const airRaidCount = corridorThreats.filter((e) => e.event_type === "air_raid").length;

    let safetyScore = 10;
    corridorThreats.forEach((e) => {
      const w = e.event_type === "missile" ? 2 : e.event_type === "air_raid" ? 1.5 : 1;
      const proximity = 1 - e.distToRoute / CORRIDOR_KM;
      safetyScore -= w * proximity * 0.5;
    });
    safetyScore = Math.max(0, Math.min(10, safetyScore));

    const recommendation =
      safetyScore >= 7 ? "GENERALLY SAFE" :
      safetyScore >= 4 ? "EXERCISE CAUTION" :
      "AVOID — HIGH RISK";

    const recColor =
      safetyScore >= 7 ? "hsl(var(--success))" :
      safetyScore >= 4 ? "hsl(var(--warning))" :
      "hsl(var(--danger))";

    // Determine safest direction
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    const dirThreats = directions.map((dir, i) => {
      const angle = i * 45 * (Math.PI / 180);
      const checkLat = fromLoc.lat + 1.5 * Math.cos(angle);
      const checkLng = fromLoc.lng + 1.5 * Math.sin(angle);
      const threats = events.filter((e) => {
        if (!e.lat || !e.lng) return false;
        return haversine(checkLat, checkLng, e.lat, e.lng) < 100;
      }).length;
      return { dir, threats };
    });
    const safestDir = dirThreats.sort((a, b) => a.threats - b.threats)[0];

    return {
      directDist,
      corridorThreats,
      missileCount,
      droneCount,
      airRaidCount,
      safetyScore: parseFloat(safetyScore.toFixed(1)),
      recommendation,
      recColor,
      safestDirection: safestDir.dir,
      safestDirThreats: safestDir.threats,
    };
  }, [fromLoc, toLoc, events]);

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-success/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">🛤️ Safe Routes & Evacuation</h2>
              <p className="mb-6 text-muted-foreground max-w-lg mx-auto">
                Check route safety and use preset evacuation corridors for conflict zones.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Safe Routes
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "safe-routes", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Route className="h-6 w-6 text-success" /> Safe Routes & Evacuation
          </h2>
          <p className="text-sm text-muted-foreground">
            Assess threats along your travel corridor (100 km buffer) or use preset evacuation routes
          </p>
        </div>

        {/* Evacuation Corridors */}
        <div className="mb-6">
          <h3 className="mb-2 font-mono text-xs uppercase text-muted-foreground flex items-center gap-1">
            <Compass className="h-3 w-3" /> Preset Evacuation Corridors
          </h3>
          <div className="flex flex-wrap gap-2">
            {EVACUATION_CORRIDORS.map((c) => (
              <Button
                key={c.name}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => useEvacCorridor(c)}
              >
                {c.name}
              </Button>
            ))}
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">Origin</label>
            <Input
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && resolveRoute()}
              placeholder="e.g. Lviv"
              className="bg-background"
            />
            {fromLoc && <p className="mt-1 text-xs text-primary">📍 {fromLoc.name}</p>}
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">Destination</label>
            <Input
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && resolveRoute()}
              placeholder="e.g. Kyiv"
              className="bg-background"
            />
            {toLoc && <p className="mt-1 text-xs text-primary">📍 {toLoc.name}</p>}
          </div>
          <div className="flex items-end">
            <Button onClick={resolveRoute} disabled={loading} className="w-full bg-primary text-primary-foreground">
              <Shield className="mr-2 h-4 w-4" /> {loading ? "Analyzing..." : "Check Route Safety"}
            </Button>
          </div>
        </div>

        {fromLoc && toLoc && analysis && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-lg border bg-card p-6 text-center" style={{ borderColor: analysis.recColor }}>
                <div className="font-mono text-xs uppercase text-muted-foreground mb-2">Route Safety</div>
                <div className="text-2xl font-black" style={{ color: analysis.recColor }}>
                  {analysis.recommendation}
                </div>
                <div className="mt-2 font-mono text-sm text-muted-foreground">
                  Score: {analysis.safetyScore} / 10
                </div>
                <div className="mx-auto mt-3 h-2 w-full max-w-[200px] overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${(analysis.safetyScore / 10) * 100}%`, background: analysis.recColor }}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-6">
                <div className="font-mono text-xs uppercase text-muted-foreground mb-2">Route Details</div>
                <p className="text-lg font-bold text-foreground">{fromLoc.name} → {toLoc.name}</p>
                <p className="font-mono text-sm text-muted-foreground mt-1">
                  📏 {analysis.directDist.toFixed(0)} km direct distance
                </p>
                <p className="font-mono text-sm text-muted-foreground">
                  ⚠️ {analysis.corridorThreats.length} threats in corridor
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-6">
                <div className="font-mono text-xs uppercase text-muted-foreground mb-2">Threat Breakdown</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">🚀 Missiles</span>
                    <span className="font-mono text-destructive">{analysis.missileCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">🛩️ Drones</span>
                    <span className="font-mono text-warning">{analysis.droneCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">🚨 Air Raids</span>
                    <span className="font-mono text-danger">{analysis.airRaidCount}</span>
                  </div>
                </div>
              </div>

              {/* Safest Direction */}
              <div className="rounded-lg border border-primary/30 bg-card p-6 text-center">
                <div className="font-mono text-xs uppercase text-muted-foreground mb-2">
                  <Compass className="inline h-3 w-3 mr-1" /> Safest Direction
                </div>
                <div className="text-3xl font-black text-primary">{analysis.safestDirection}</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {analysis.safestDirThreats === 0 ? "No threats detected" : `${analysis.safestDirThreats} threats in sector`}
                </p>
              </div>
            </div>

            {analysis.corridorThreats.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-3 font-mono text-xs uppercase text-muted-foreground">
                  Nearest threats along route ({Math.min(analysis.corridorThreats.length, 8)} shown)
                </h3>
                <div className="space-y-2">
                  {analysis.corridorThreats.slice(0, 8).map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded border border-border/50 bg-background px-3 py-2">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="font-mono text-xs uppercase">{e.event_type}</Badge>
                        <span className="text-sm text-foreground">{e.title.slice(0, 50)}</span>
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">{e.distToRoute.toFixed(0)} km from route</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!fromLoc && !toLoc && (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
            <Route className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">Select an evacuation corridor above or enter custom locations</p>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default SafeRoutes;
